import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useParams, useSearchParams } from 'react-router';
import { api } from '../../api/client';
import type { TicketDetail } from '../../api/ticket-types';
import { useCrumbs } from '../../layout/crumbs';
import { QueryView } from '../../ui/feedback';
import { Tabs, usePageTitle } from '../../ui/misc';
import { TicketActionDialog, type DialogAction } from './ActionDialog';
import { Approvals } from './detail/Approvals';
import { Delivery } from './detail/Delivery';
import { Discussion } from './detail/Discussion';
import { EditScope } from './detail/Editing';
import { Header, Trace } from './detail/Header';
import { History } from './detail/History';
import { Implementation } from './detail/Implementation';
import { Overview } from './detail/Overview';
import { Side } from './detail/Sidebar';

export type TicketTab = 'overview' | 'discussion' | 'implementation' | 'approvals' | 'delivery' | 'history';

export function useTicketDetail(projectKey: string, number: number) {
  return useQuery({
    queryKey: ['ticket', projectKey, number],
    queryFn: () => api.get<TicketDetail>(`/projects/${projectKey}/tickets/${number}`),
    refetchInterval: (query) =>
      query.state.data?.attachments.some((a) => !a.removedAt && a.scanState === 'pending') ? 2000 : false,
  });
}

/** TKT-02: full scope, state, relationships, discussion, approvals, implementation and delivery evidence. */
export function TicketDetailPage() {
  const { key, number } = useParams();
  const n = Number(number);
  const [params, setParams] = useSearchParams();
  const tab = (params.get('tab') as TicketTab) || 'overview';
  const q = useTicketDetail(key!, n);
  usePageTitle(q.data ? `${q.data.key} · ${q.data.title}` : null);
  useCrumbs([
    { label: q.data?.project.name ?? key ?? '', to: `/projects/${key}` },
    { label: 'Board', to: `/projects/${key}/board` },
    { label: q.data?.key ?? `${key}-${n}` },
  ]);
  return (
    <div className="page">
      <TicketDetailView
        projectKey={key!}
        number={n}
        tab={tab}
        onTabChange={(v) => setParams(v === 'overview' ? {} : { tab: v }, { replace: true })}
      />
    </div>
  );
}

/** Shared workspace; presentation controls navigation and page metadata. */
export function TicketDetailView({
  projectKey,
  number,
  tab,
  onTabChange,
}: {
  projectKey: string;
  number: number;
  tab: TicketTab;
  onTabChange: (tab: TicketTab) => void;
}) {
  const q = useTicketDetail(projectKey, number);
  const [dialog, setDialog] = useState<DialogAction | null>(null);
  const [editing, setEditing] = useState(false);
  return (
    <div className="ticket-workspace">
      <QueryView query={q}>
        {(t) => (
          <>
            <Header t={t} onAction={setDialog} onEdit={() => setEditing(true)} />
            <Trace t={t} />
            <Tabs<TicketTab>
              label="Ticket sections"
              value={tab}
              onChange={onTabChange}
              tabs={[
                { value: 'overview', label: 'Overview' },
                { value: 'discussion', label: 'Discussion', count: t.commentCount },
                { value: 'implementation', label: 'Implementation', count: t.implementations.length },
                { value: 'approvals', label: 'Approvals', count: t.approvals.length + t.acceptances.length },
                { value: 'delivery', label: 'Releases and delivery', count: t.releases.length },
                { value: 'history', label: 'History' },
              ]}
            />
            <div className="split">
              <div className="stack lg" style={{ minWidth: 0 }}>
                {tab === 'overview' && <Overview t={t} />}
                {tab === 'discussion' && <Discussion t={t} />}
                {tab === 'implementation' && (
                  <Implementation t={t} onStart={() => setDialog('start_manual')} />
                )}
                {tab === 'approvals' && <Approvals t={t} />}
                {tab === 'delivery' && <Delivery t={t} />}
                {tab === 'history' && <History t={t} />}
              </div>
              <Side t={t} />
            </div>
            {dialog && (
              <TicketActionDialog
                projectKey={projectKey}
                number={number}
                action={dialog}
                onClose={() => setDialog(null)}
              />
            )}
            {editing && <EditScope t={t} onClose={() => setEditing(false)} />}
          </>
        )}
      </QueryView>
    </div>
  );
}
